import { formatInTimeZone } from 'date-fns-tz';
import { Fragment, useState } from 'react';

import {
    cumulativeUpToSameHour,
    sameTimeDeltaPct,
} from '@apps/web/components/admin/outreach_observability/comparison_utils';
import {
    OUTREACH_ANALYTICS_TIMEZONE,
    type OutreachUrgencyFilter,
    type OutreachWindowSelection,
} from '@apps/web/components/admin/outreach_observability/constants';
import {
    KpiSparkline,
    KpiTrendDialog,
    type SparklineMetricKey,
} from '@apps/web/components/admin/outreach_observability/kpi_sparkline';
import {
    KpiStatCard,
    type KpiStatDelta,
} from '@apps/web/components/admin/outreach_observability/kpi_stat_card';
import { RunsDrillDialog } from '@apps/web/components/admin/outreach_observability/runs_drill_dialog';
import { type KpiRunMetric } from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    type ComparisonDay,
    type ComparisonsSection,
    type HourlyKpiBucket,
    type OutreachMetricFlag,
    type OutreachObservabilityPulse,
} from '@libraries/typescript/api_client';

type PulseStripProps = {
    readonly pulse: OutreachObservabilityPulse;
    readonly metricFlags: Array<OutreachMetricFlag>;
    // Compact window phrase ("trailing 24h" / "today") for the runs label.
    readonly windowSummary: string;
    // The live window the pulse counts reflect, forwarded to the non-sparkline
    // tile drill-downs so each dialog counts over the same window as its tile.
    readonly window: OutreachWindowSelection;
    // Hourly (trailing 48h) buckets powering the per-tile sparklines.
    readonly kpiHourlyTrends: Array<HourlyKpiBucket>;
    // Immutable yesterday / 7-days-ago comparison days for the KPI deltas.
    // Null while the section is missing; deltas are then omitted.
    readonly comparisons: ComparisonsSection | null;
    // Org filter from the page, forwarded to the modal's run drill-down.
    readonly organizationIds: Array<string>;
    readonly urgency: OutreachUrgencyFilter;
};

function formatPercent(numerator: number, denominator: number): string {
    if (denominator === 0) {
        return '0%';
    }
    return `${((numerator / denominator) * 100).toFixed(1)}%`;
}

function formatCostUsd(cost: number): string {
    return `$${cost.toFixed(2)}`;
}

function etDateKey(iso: string): string {
    return formatInTimeZone(
        new Date(iso),
        OUTREACH_ANALYTICS_TIMEZONE,
        'yyyy-MM-dd',
    );
}

function ratio(numerator: number, denominator: number): number | null {
    return denominator === 0 ? null : numerator / denominator;
}

// Shared accessor shape covering both today's HourlyKpiBucket and the immutable
// ComparisonHourBucket — the fields the deltas read exist on both.
type DeltaCountable = {
    readonly num_runs: number;
    readonly cancelled_run_count: number;
    readonly covered_shift_count: number;
    readonly yes_shift_count: number;
    readonly filled_shift_count: number;
    readonly cost_usd: number;
};

// KPI deltas compare today's cumulative-up-to-this-ET-hour against the same
// slice of an immutable comparison day, so a partial "today" is never judged
// against a full day. Count/amount metrics (runs, cost) use a percent change;
// rate metrics (% yes, % filled, % cancelled) use a percentage-POINT difference
// so "43% vs 40%" reads as +3pp, not +7.5%.
function buildDeltas(
    todayBuckets: Array<HourlyKpiBucket>,
    comparisons: ComparisonsSection,
    nowEtHour: number,
    kind: 'runs' | 'shifts_yes' | 'shifts_filled' | 'runs_cancelled' | 'cost',
): Array<KpiStatDelta> {
    const periods: Array<{
        readonly label: string;
        readonly day: ComparisonDay;
    }> = [
        { label: 'vs yest', day: comparisons.yesterday },
        { label: 'vs 7d', day: comparisons.week_ago },
    ];
    return periods.map(({ label, day }) => {
        const todayValue = (
            field: (bucket: DeltaCountable) => number,
        ): number => cumulativeUpToSameHour(todayBuckets, field, nowEtHour);
        const compValue = (field: (bucket: DeltaCountable) => number): number =>
            cumulativeUpToSameHour(day.hourly, field, nowEtHour);
        switch (kind) {
            case 'runs':
                return {
                    label,
                    pct: sameTimeDeltaPct(
                        todayValue((bucket) => bucket.num_runs),
                        compValue((bucket) => bucket.num_runs),
                    ),
                    goodWhenUp: true,
                };
            case 'cost':
                return {
                    label,
                    pct: sameTimeDeltaPct(
                        todayValue((bucket) => bucket.cost_usd),
                        compValue((bucket) => bucket.cost_usd),
                    ),
                    goodWhenUp: false,
                };
            case 'shifts_yes':
                return pointDelta(
                    label,
                    ratio(
                        todayValue((bucket) => bucket.yes_shift_count),
                        todayValue((bucket) => bucket.covered_shift_count),
                    ),
                    ratio(
                        compValue((bucket) => bucket.yes_shift_count),
                        compValue((bucket) => bucket.covered_shift_count),
                    ),
                    true,
                );
            case 'shifts_filled':
                return pointDelta(
                    label,
                    ratio(
                        todayValue((bucket) => bucket.filled_shift_count),
                        todayValue((bucket) => bucket.covered_shift_count),
                    ),
                    ratio(
                        compValue((bucket) => bucket.filled_shift_count),
                        compValue((bucket) => bucket.covered_shift_count),
                    ),
                    true,
                );
            case 'runs_cancelled':
                return pointDelta(
                    label,
                    ratio(
                        todayValue((bucket) => bucket.cancelled_run_count),
                        todayValue((bucket) => bucket.num_runs),
                    ),
                    ratio(
                        compValue((bucket) => bucket.cancelled_run_count),
                        compValue((bucket) => bucket.num_runs),
                    ),
                    false,
                );
            default:
                return { label, pct: null, goodWhenUp: true };
        }
    });
}

// Cost per filled shift ("cost pace" over the comparison cost cumulative divided
// by filled cumulative): a percent change of the two ratios, lower is better.
function buildCostPerFilledDeltas(
    todayBuckets: Array<HourlyKpiBucket>,
    comparisons: ComparisonsSection,
    nowEtHour: number,
): Array<KpiStatDelta> {
    const periods: Array<{
        readonly label: string;
        readonly day: ComparisonDay;
    }> = [
        { label: 'vs yest', day: comparisons.yesterday },
        { label: 'vs 7d', day: comparisons.week_ago },
    ];
    return periods.map(({ label, day }) => {
        const todayPer = ratio(
            cumulativeUpToSameHour(
                todayBuckets,
                (bucket) => bucket.cost_usd,
                nowEtHour,
            ),
            cumulativeUpToSameHour(
                todayBuckets,
                (bucket) => bucket.filled_shift_count,
                nowEtHour,
            ),
        );
        const compPer = ratio(
            cumulativeUpToSameHour(
                day.hourly,
                (bucket) => bucket.cost_usd,
                nowEtHour,
            ),
            cumulativeUpToSameHour(
                day.hourly,
                (bucket) => bucket.filled_shift_count,
                nowEtHour,
            ),
        );
        return {
            label,
            pct:
                todayPer === null || compPer === null
                    ? null
                    : sameTimeDeltaPct(todayPer, compPer),
            goodWhenUp: false,
        };
    });
}

function pointDelta(
    label: string,
    todayRate: number | null,
    comparisonRate: number | null,
    goodWhenUp: boolean,
): KpiStatDelta {
    if (todayRate === null || comparisonRate === null) {
        return { label, pct: null, goodWhenUp, format: 'points' };
    }
    return {
        label,
        pct: (todayRate - comparisonRate) * 100,
        goodWhenUp,
        format: 'points',
    };
}

// The card severity flag carries only level here; the baseline phrase rides the
// `sub` line so it stays visible (muted) even when the metric is not flagged.
function toStatFlag(flag: OutreachMetricFlag | undefined) {
    if (flag === undefined || flag.level === 'ok') {
        return undefined;
    }
    return { level: flag.level, description: '' };
}

// A pending KPI drill-down: the metric to fetch runs for, the modal title, and
// (for the five sparklined tiles) which trend line to chart. When a
// sparklineMetric is set and hourly data exists, the drill renders the trend
// chart above the runs table; otherwise it is a table-only drill.
type OpenDrill = {
    readonly metric: KpiRunMetric;
    readonly title: string;
    readonly description?: string;
    readonly sparklineMetric?: SparklineMetricKey;
};

export function PulseStrip({
    pulse,
    metricFlags,
    windowSummary,
    window: liveWindow,
    kpiHourlyTrends,
    comparisons,
    organizationIds,
    urgency,
}: PulseStripProps) {
    const [openDrill, setOpenDrill] = useState<OpenDrill | null>(null);
    const flagByMetric = new Map(
        metricFlags.map((flag) => [flag.metric, flag]),
    );
    const hasSparklineData = kpiHourlyTrends.length > 0;

    // Deltas anchor on today's ET calendar day only; the trailing-48h series
    // also carries yesterday's buckets, which share the same hour-of-day and
    // would otherwise be summed into today's cumulative. Anchored on the
    // payload's window_end (like the funnel deltas) rather than browser time,
    // so both surfaces agree on "same time of day" even with refresh lag or
    // near an hour boundary.
    const nowEtHour = Number(
        formatInTimeZone(
            new Date(pulse.window_end),
            OUTREACH_ANALYTICS_TIMEZONE,
            'H',
        ),
    );
    const todayEtKey = etDateKey(pulse.window_end);
    const todayBuckets = kpiHourlyTrends.filter(
        (bucket) => etDateKey(bucket.bucket_start) === todayEtKey,
    );
    const deltasFor = (
        kind:
            | 'runs'
            | 'shifts_yes'
            | 'shifts_filled'
            | 'runs_cancelled'
            | 'cost',
    ): Array<KpiStatDelta> | undefined =>
        comparisons
            ? buildDeltas(todayBuckets, comparisons, nowEtHour, kind)
            : undefined;
    const costPerFilledDeltas = comparisons
        ? buildCostPerFilledDeltas(todayBuckets, comparisons, nowEtHour)
        : undefined;
    const costPerFilledValue =
        pulse.filled_shift_count === 0
            ? '—'
            : formatCostUsd(pulse.total_cost_usd / pulse.filled_shift_count);

    // A tile only shows its sparkline when there are hourly buckets to plot.
    function sparklineNode(metric: SparklineMetricKey) {
        if (!hasSparklineData) {
            return undefined;
        }
        return <KpiSparkline buckets={kpiHourlyTrends} metricKey={metric} />;
    }

    // Every KPI tile is clickable, so this always returns a handler. The drill
    // renders the trend chart only when the tile is sparklined and hourly data
    // exists; otherwise it is a table-only runs drill.
    function openDrillHandler(drill: OpenDrill) {
        return () => setOpenDrill(drill);
    }

    function flaggedCard(
        flagMetric: OutreachMetricFlag['metric'],
        label: string,
        value: number,
        drill: {
            readonly metric: KpiRunMetric;
            readonly description?: string;
            readonly sparklineMetric?: SparklineMetricKey;
        },
    ) {
        const flag = flagByMetric.get(flagMetric);
        return (
            <KpiStatCard
                label={label}
                value={String(value)}
                sub={
                    flag
                        ? `vs ${flag.baseline_daily_mean.toFixed(1)}/day avg`
                        : undefined
                }
                flag={toStatFlag(flag)}
                sparkline={
                    drill.sparklineMetric
                        ? sparklineNode(drill.sparklineMetric)
                        : undefined
                }
                onClick={openDrillHandler({
                    metric: drill.metric,
                    title: label,
                    description: drill.description,
                    sparklineMetric: drill.sparklineMetric,
                })}
            />
        );
    }

    return (
        <Fragment>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                <KpiStatCard
                    label={`Runs (${windowSummary})`}
                    value={String(pulse.num_runs)}
                    deltas={deltasFor('runs')}
                    sparkline={sparklineNode('runs')}
                    onClick={openDrillHandler({
                        metric: 'runs',
                        title: `Runs (${windowSummary})`,
                        sparklineMetric: 'runs',
                    })}
                />
                <KpiStatCard
                    label="% shifts yes"
                    value={formatPercent(
                        pulse.yes_shift_count,
                        pulse.covered_shift_count,
                    )}
                    sub={`${pulse.yes_shift_count}/${pulse.covered_shift_count} shifts`}
                    deltas={deltasFor('shifts_yes')}
                    sparkline={sparklineNode('shifts_yes')}
                    onClick={openDrillHandler({
                        metric: 'shifts_yes',
                        title: '% shifts yes',
                        sparklineMetric: 'shifts_yes',
                    })}
                />
                <KpiStatCard
                    label="% shifts filled"
                    value={formatPercent(
                        pulse.filled_shift_count,
                        pulse.covered_shift_count,
                    )}
                    sub={`${pulse.filled_shift_count}/${pulse.covered_shift_count} shifts`}
                    deltas={deltasFor('shifts_filled')}
                    onClick={openDrillHandler({
                        metric: 'shifts_filled',
                        title: '% shifts filled',
                        description: `Runs with at least one filled shift (${windowSummary}).`,
                    })}
                />
                <KpiStatCard
                    label="% shifts cancelled"
                    value={formatPercent(
                        pulse.cancelled_shift_count,
                        pulse.covered_shift_count,
                    )}
                    sub={`${pulse.cancelled_shift_count}/${pulse.covered_shift_count} shifts`}
                    onClick={openDrillHandler({
                        metric: 'shifts_cancelled',
                        title: '% shifts cancelled',
                        description: `Runs with at least one cancelled shift (${windowSummary}).`,
                    })}
                />
                <KpiStatCard
                    label="% runs cancelled"
                    value={formatPercent(pulse.cancelled_count, pulse.num_runs)}
                    sub={`${pulse.cancelled_count} runs`}
                    deltas={deltasFor('runs_cancelled')}
                    sparkline={sparklineNode('runs_cancelled')}
                    onClick={openDrillHandler({
                        metric: 'runs_cancelled',
                        title: '% runs cancelled',
                        sparklineMetric: 'runs_cancelled',
                    })}
                />
                <KpiStatCard
                    label={`Cost (${windowSummary})`}
                    value={formatCostUsd(pulse.total_cost_usd)}
                    deltas={deltasFor('cost')}
                    onClick={openDrillHandler({
                        metric: 'cost',
                        title: `Cost (${windowSummary})`,
                        description: `Top spend runs, highest cost first (${windowSummary}).`,
                    })}
                />
                <KpiStatCard
                    label="Cost / filled shift"
                    value={costPerFilledValue}
                    sub={`${pulse.filled_shift_count} filled`}
                    deltas={costPerFilledDeltas}
                    onClick={openDrillHandler({
                        metric: 'cost',
                        title: 'Cost / filled shift',
                        description: `Top spend runs, highest cost first (${windowSummary}).`,
                    })}
                />
                <KpiStatCard
                    label="In progress"
                    value={String(pulse.in_progress_count)}
                    sub={`${pulse.unfilled_count} unfilled`}
                    onClick={openDrillHandler({
                        metric: 'in_progress',
                        title: 'In-progress runs',
                        description: `In-progress runs (${windowSummary}).`,
                    })}
                />
                {flaggedCard(
                    'zero_caregiver_suggestions',
                    '0-suggestion runs',
                    pulse.zero_caregiver_suggestion_count,
                    {
                        metric: 'zero_suggestions',
                        description: `Runs with zero suggested caregivers (${windowSummary}).`,
                    },
                )}
                {flaggedCard(
                    'tool_failures',
                    'Tool-failure runs',
                    pulse.tool_failure_run_count,
                    {
                        metric: 'tool_failures',
                        description: `Runs with at least one failed tool call (${windowSummary}).`,
                    },
                )}
                {flaggedCard(
                    'compaction',
                    'Compaction runs',
                    pulse.compaction_run_count,
                    { metric: 'compaction', sparklineMetric: 'compaction' },
                )}
                {flaggedCard(
                    'stale_idle',
                    'Stale / abandoned',
                    pulse.stale_idle_count,
                    { metric: 'stale', sparklineMetric: 'stale' },
                )}
                <KpiStatCard
                    label="Failed runs"
                    value={String(pulse.failed_run_count)}
                    sub={`${pulse.cancelled_before_contact_count} cancelled pre-contact`}
                    onClick={openDrillHandler({
                        metric: 'failed_runs',
                        title: 'Failed runs',
                        description: `Failed runs (${windowSummary}).`,
                    })}
                />
            </div>
            {openDrill ? (
                openDrill.sparklineMetric && hasSparklineData ? (
                    <KpiTrendDialog
                        open={true}
                        onOpenChange={(next) => {
                            if (!next) {
                                setOpenDrill(null);
                            }
                        }}
                        buckets={kpiHourlyTrends}
                        metricKey={openDrill.sparklineMetric}
                        organizationIds={organizationIds}
                        urgency={urgency}
                    />
                ) : (
                    <RunsDrillDialog
                        open={true}
                        onOpenChange={(next) => {
                            if (!next) {
                                setOpenDrill(null);
                            }
                        }}
                        title={openDrill.title}
                        description={openDrill.description}
                        metric={openDrill.metric}
                        organizationIds={organizationIds}
                        urgency={urgency}
                        // Non-sparkline tiles count over the live window, so scope
                        // their dialog to it. A sparkline tile only lands here
                        // when it has no hourly data to chart; it keeps the
                        // default 48h window to match the sparkline drill-down.
                        filters={
                            openDrill.sparklineMetric
                                ? undefined
                                : { window: liveWindow }
                        }
                    />
                )
            ) : null}
        </Fragment>
    );
}
